import { useEffect, useRef, useState } from 'react';

const SIN_PERMISO = 'No tenés permiso para realizar esta acción';

export interface OpcionImportar {
  etiqueta: string;
  descripcion?: string;
  /** Opción que pide un archivo: abre el selector nativo y entrega el archivo elegido. */
  archivo?: { accept: string; onArchivo: (archivo: File) => void };
  onClick?: () => void;
  deshabilitado?: boolean;
  /** Por qué está deshabilitada. Por defecto, falta de permiso. */
  motivo?: string;
}

interface Props {
  opciones: OpcionImportar[];
  /** Hay un import en curso: el botón lo indica y no se puede abrir. */
  ocupado?: boolean;
  /**
   * Borde del botón al que se alinea el menú. "derecha" (por defecto) lo abre hacia la izquierda;
   * usar "izquierda" cuando el botón está cerca del borde izquierdo (si no, queda bajo el menú lateral).
   */
  alinear?: 'izquierda' | 'derecha';
}

/** Un único botón "Importar" que despliega las formas de importar de la pantalla. */
export function MenuImportar({ opciones, ocupado = false, alinear = 'derecha' }: Props) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const todasDeshabilitadas = opciones.every(o => o.deshabilitado);
  const deshabilitado = ocupado || todasDeshabilitadas;

  useEffect(() => {
    if (!abierto) return;
    const alClicFuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setAbierto(false); boton.current?.focus(); }
    };
    document.addEventListener('mousedown', alClicFuera);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alClicFuera);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto]);

  const elegir = (opcion: OpcionImportar, i: number) => {
    setAbierto(false);
    if (opcion.archivo) inputs.current[i]?.click();
    else opcion.onClick?.();
  };

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={boton}
        type="button"
        onClick={() => setAbierto(a => !a)}
        disabled={deshabilitado}
        aria-haspopup="menu"
        aria-expanded={abierto}
        title={todasDeshabilitadas && !ocupado ? SIN_PERMISO : undefined}
        className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {ocupado ? '⏳ Procesando...' : 'Importar ▾'}
      </button>
      {/* Siempre montados: el menú se cierra al elegir, pero el selector de archivos sigue abierto. */}
      {opciones.map((o, i) => o.archivo && (
        <input
          key={o.etiqueta}
          ref={el => { inputs.current[i] = el; }}
          type="file"
          accept={o.archivo.accept}
          aria-label={o.etiqueta}
          disabled={o.deshabilitado || ocupado}
          tabIndex={-1}
          className="hidden"
          onChange={e => {
            const archivo = e.target.files?.[0];
            e.target.value = '';
            if (archivo) o.archivo!.onArchivo(archivo);
          }}
        />
      ))}
      {abierto && (
        <div role="menu" aria-label="Importar" className={`absolute ${alinear === 'izquierda' ? 'left-0' : 'right-0'} mt-1 z-30 w-72 bg-white border border-slate-200 rounded-lg shadow-lg py-1`}>
          {opciones.map((o, i) => (
            <button
              key={o.etiqueta}
              type="button"
              role="menuitem"
              onClick={() => elegir(o, i)}
              disabled={o.deshabilitado}
              title={o.deshabilitado ? (o.motivo ?? SIN_PERMISO) : undefined}
              className="block w-full text-left px-3 py-2 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <span className="block text-sm font-medium text-slate-700">{o.etiqueta}</span>
              {o.descripcion && <span className="block text-xs text-slate-500">{o.descripcion}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
